import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  FileUp,
  Filter,
  FolderOpen,
  Trash2,
  XCircle,
  Calendar,
  Sparkles,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { apiRequest, API_BASE } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const CATEGORY_LABELS = {
  nomina: "Nomine",
  attestato: "Attestati formazione",
  planimetria: "Planimetrie",
  certificazione: "Certificazioni",
  sds: "SDS sostanze",
  manutenzione: "Manutenzioni",
  verbale: "Verbali",
  relazione_tecnica: "Relazioni tecniche",
  dpi: "DPI",
  altro: "Altro",
  dvr: "DVR",
  nomine: "Nomine (legacy)",
  formazione: "Formazione (legacy)",
  sorveglianza_sanitaria: "Sorveglianza sanitaria",
  attrezzature: "Attrezzature",
  sostanze: "Sostanze",
  appalti_duvri: "Appalti / DUVRI",
  planimetrie: "Planimetrie (legacy)",
  certificazioni: "Certificazioni (legacy)",
  contratti: "Contratti",
  verbali_consultazione: "Verbali consultazione (legacy)",
};

const REQUIRED_CATEGORIES = [
  "nomina", "attestato", "planimetria", "certificazione",
  "sds", "manutenzione", "verbale", "dpi",
];

const STATUS_TONE = {
  ok: "bg-emerald-100 text-emerald-700 border-emerald-200",
  expiring: "bg-amber-100 text-amber-800 border-amber-200",
  expired: "bg-red-100 text-red-700 border-red-200",
  missing: "bg-slate-100 text-slate-600 border-slate-200",
  no_expiry: "bg-slate-50 text-slate-600 border-slate-200",
};

const EMPTY_FORM = {
  document_name: "",
  category: "nomina",
  description: "",
  expiry_date: "",
  related_worker_id: "",
  related_equipment_id: "",
  related_substance_id: "",
  related_risk_code: "",
  tags: "",
};

function StatusBadge({ status, label }) {
  const map = {
    ok: "OK",
    expiring: "In scadenza",
    expired: "Scaduto",
    missing: "Mancante",
    no_expiry: "Senza scadenza",
  };
  return (
    <Badge className={STATUS_TONE[status] || STATUS_TONE.no_expiry}>
      {label || map[status] || status}
    </Badge>
  );
}

export default function CompanyDocuments() {
  const { id } = useParams();
  const [docs, setDocs] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = async () => {
    setLoading(true);
    setError("");
    try {
      const q = new URLSearchParams();
      if (filterCat) q.set("category", filterCat);
      if (filterStatus) q.set("expiry_status", filterStatus);
      const [docsRes, checklistRes] = await Promise.all([
        apiRequest(`/companies/${id}/documents${q.toString() ? `?${q}` : ""}`),
        apiRequest(`/companies/${id}/documents/missing-checklist`),
      ]);
      setDocs(docsRes);
      setChecklist(checklistRes.checklist || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, filterCat, filterStatus]);

  const expiringSoon = useMemo(
    () => docs.filter((d) => d.expiry_status === "expiring" || d.expiry_status === "expired"),
    [docs],
  );

  const missingCount = useMemo(
    () => checklist.filter((c) => c.status === "missing").length,
    [checklist],
  );

  const upload = async (e) => {
    e?.preventDefault();
    if (!file) {
      toast.error("Seleziona un file da caricare");
      return;
    }
    if (!form.document_name || !form.category) {
      toast.error("Nome e categoria obbligatori");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => {
        if (v) fd.append(k, v);
      });
      const token = localStorage.getItem("dvr_token");
      const r = await fetch(`${API_BASE}/companies/${id}/documents`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt);
      }
      toast.success("Documento caricato");
      setShowUpload(false);
      setFile(null);
      setForm(EMPTY_FORM);
      reload();
    } catch (err) {
      toast.error(`Upload fallito: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (docId) => {
    if (!window.confirm("Eliminare il documento?")) return;
    try {
      await apiRequest(`/companies/${id}/documents/${docId}`, { method: "DELETE" });
      toast.success("Documento eliminato");
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const token = localStorage.getItem("dvr_token");
      const r = await fetch(`${API_BASE}/companies/${id}/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(`Download fallito: ${err.message}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="company-documents-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button asChild variant="outline" data-testid="documents-back-button">
          <Link to={`/aziende/${id}`}>
            <ArrowLeft className="h-4 w-4" /> Scheda azienda
          </Link>
        </Button>
        <Button
          className="bg-blue-900 hover:bg-blue-800"
          onClick={() => setShowUpload(true)}
          data-testid="documents-upload-button"
        >
          <FileUp className="h-4 w-4" /> Carica documento
        </Button>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">
          <FolderOpen className="mr-1 inline h-3 w-3" /> Archivio documentale
        </p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">
          Documenti dell'azienda
        </h2>
        <p className="mt-2 text-slate-600">
          Repository centralizzato di nomine, attestati, planimetrie, SDS,
          certificazioni, verbali e relazioni tecniche. Gli allegati vengono
          inclusi automaticamente nell'export DVR.
        </p>
      </section>

      {/* Missing checklist */}
      {missingCount > 0 && (
        <Alert
          className="border-amber-200 bg-amber-50"
          data-testid="documents-missing-checklist-alert"
        >
          <ShieldAlert className="h-4 w-4 text-amber-700" />
          <AlertTitle>{missingCount} categorie obbligatorie mancanti</AlertTitle>
          <AlertDescription>
            Le seguenti categorie non hanno alcun documento caricato:{" "}
            {checklist
              .filter((c) => c.status === "missing")
              .map((c) => CATEGORY_LABELS[c.category] || c.category)
              .join(", ")}
          </AlertDescription>
        </Alert>
      )}

      {/* Expiring soon */}
      {expiringSoon.length > 0 && (
        <Alert
          className="border-red-200 bg-red-50"
          data-testid="documents-expiring-alert"
        >
          <Clock className="h-4 w-4 text-red-700" />
          <AlertTitle>
            {expiringSoon.length} documenti scaduti o in scadenza
          </AlertTitle>
          <AlertDescription>
            Verificare e rinnovare i documenti con scadenza ≤30 giorni.
          </AlertDescription>
        </Alert>
      )}

      {/* Compliance checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">
            <Sparkles className="mr-1 inline h-4 w-4 text-blue-900" />
            Checklist conformità D.Lgs. 81/2008
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
            data-testid="documents-checklist-grid"
          >
            {checklist.map((c) => (
              <div
                key={c.category}
                className="rounded-md border border-slate-200 bg-white p-3"
                data-testid={`checklist-${c.category}`}
              >
                <p className="text-xs font-semibold uppercase text-slate-500">
                  {CATEGORY_LABELS[c.category] || c.category}
                </p>
                <p className="mt-1 text-xs text-slate-500">{c.label}</p>
                <div className="mt-2 flex items-center justify-between">
                  <StatusBadge status={c.status} />
                  <span className="text-xs text-slate-500">
                    {c.valid}/{c.total || "-"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div
        className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white p-3"
        data-testid="documents-filter-bar"
      >
        <Filter className="h-4 w-4 text-slate-500" />
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
          data-testid="documents-filter-category"
        >
          <option value="">Tutte le categorie</option>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
          data-testid="documents-filter-status"
        >
          <option value="">Tutti gli stati</option>
          <option value="ok">Validi</option>
          <option value="expiring">In scadenza</option>
          <option value="expired">Scaduti</option>
          <option value="no_expiry">Senza scadenza</option>
        </select>
        <span className="ml-auto text-xs text-slate-500">{docs.length} documenti</span>
      </div>

      {error && (
        <Alert variant="destructive" data-testid="documents-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Errore</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Documents table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-center text-sm text-slate-500">Caricamento…</p>
          ) : docs.length === 0 ? (
            <p
              className="p-6 text-center text-sm text-slate-500"
              data-testid="documents-empty"
            >
              <FileText className="mx-auto mb-2 h-8 w-8 text-slate-300" />
              Nessun documento. Carica il primo per popolare l'archivio.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full min-w-[800px] text-sm"
                data-testid="documents-table"
              >
                <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="p-3">Documento</th>
                    <th className="p-3">Categoria</th>
                    <th className="p-3">Scadenza</th>
                    <th className="p-3">Stato</th>
                    <th className="p-3">Collegamenti</th>
                    <th className="p-3">Caricato da</th>
                    <th className="p-3">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => (
                    <tr
                      key={d.id}
                      className="border-b last:border-b-0"
                      data-testid={`document-row-${d.id}`}
                    >
                      <td className="p-3">
                        <button
                          className="text-left font-semibold text-blue-900 hover:underline"
                          onClick={() => downloadDoc(d)}
                          data-testid={`document-download-${d.id}`}
                        >
                          {d.document_name}
                        </button>
                        {d.description && (
                          <p className="mt-1 text-xs text-slate-500">{d.description}</p>
                        )}
                        <p className="mt-1 text-xs text-slate-400">
                          {d.file_name} · {Math.round(d.file_size / 1024)} KB
                        </p>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline">
                          {CATEGORY_LABELS[d.category] || d.category}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs">
                        {d.expiry_date ? (
                          <span>
                            <Calendar className="mr-1 inline h-3 w-3" />
                            {d.expiry_date}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        <StatusBadge status={d.expiry_status} />
                      </td>
                      <td className="p-3 text-xs text-slate-500">
                        {d.related_risk_code && (
                          <div>Rischio: <code>{d.related_risk_code}</code></div>
                        )}
                        {d.related_worker_id && <div>Lavoratore: {d.related_worker_id.slice(0, 8)}…</div>}
                        {d.related_equipment_id && <div>Attrezz.: {d.related_equipment_id.slice(0, 8)}…</div>}
                        {d.related_substance_id && <div>Sostanza: {d.related_substance_id.slice(0, 8)}…</div>}
                        {d.dvr_assessment_id && <div>DVR: {d.dvr_assessment_id.slice(0, 8)}…</div>}
                        {!d.related_risk_code && !d.related_worker_id && !d.related_equipment_id && !d.related_substance_id && !d.dvr_assessment_id && (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="p-3 text-xs">
                        {d.uploaded_by_name || "—"}
                        <p className="text-slate-400">
                          {new Date(d.created_at).toLocaleDateString("it-IT")}
                        </p>
                      </td>
                      <td className="p-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-200 text-red-700 hover:bg-red-50"
                          onClick={() => remove(d.id)}
                          data-testid={`document-delete-${d.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent
          className="max-w-2xl"
          data-testid="documents-upload-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="h-5 w-5" /> Carica nuovo documento
            </DialogTitle>
            <DialogDescription>
              Aggiungi il file e i metadati di collegamento (rischio, lavoratore, attrezzatura).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={upload} className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label>File *</Label>
              <Input
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                required
                data-testid="documents-upload-file-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Nome documento *</Label>
              <Input
                value={form.document_name}
                onChange={(e) => setForm((p) => ({ ...p, document_name: e.target.value }))}
                required
                data-testid="documents-upload-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <select
                value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                required
                data-testid="documents-upload-category-select"
              >
                {REQUIRED_CATEGORIES.concat(["relazione_tecnica", "altro"]).map((k) => (
                  <option key={k} value={k}>
                    {CATEGORY_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Descrizione</Label>
              <textarea
                className="min-h-16 w-full rounded-md border border-slate-200 p-2 text-sm"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                data-testid="documents-upload-description-textarea"
              />
            </div>
            <div className="space-y-2">
              <Label>Data scadenza</Label>
              <Input
                type="date"
                value={form.expiry_date}
                onChange={(e) => setForm((p) => ({ ...p, expiry_date: e.target.value }))}
                data-testid="documents-upload-expiry-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Codice rischio collegato</Label>
              <Input
                placeholder="es. R-ELE-01"
                value={form.related_risk_code}
                onChange={(e) => setForm((p) => ({ ...p, related_risk_code: e.target.value }))}
                data-testid="documents-upload-risk-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>ID lavoratore (opzionale)</Label>
              <Input
                value={form.related_worker_id}
                onChange={(e) => setForm((p) => ({ ...p, related_worker_id: e.target.value }))}
                data-testid="documents-upload-worker-id-input"
              />
            </div>
            <div className="space-y-2">
              <Label>ID attrezzatura (opzionale)</Label>
              <Input
                value={form.related_equipment_id}
                onChange={(e) => setForm((p) => ({ ...p, related_equipment_id: e.target.value }))}
                data-testid="documents-upload-equipment-id-input"
              />
            </div>
            <div className="space-y-2">
              <Label>ID sostanza (opzionale)</Label>
              <Input
                value={form.related_substance_id}
                onChange={(e) => setForm((p) => ({ ...p, related_substance_id: e.target.value }))}
                data-testid="documents-upload-substance-id-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Tag (separati da virgola)</Label>
              <Input
                placeholder="es. annuale, art-71, urgente"
                value={form.tags}
                onChange={(e) => setForm((p) => ({ ...p, tags: e.target.value }))}
                data-testid="documents-upload-tags-input"
              />
            </div>
            <DialogFooter className="md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowUpload(false)}
                data-testid="documents-upload-cancel-button"
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={uploading}
                className="bg-blue-900 hover:bg-blue-800"
                data-testid="documents-upload-submit-button"
              >
                <FileUp className="mr-1 h-4 w-4" />
                {uploading ? "Caricamento…" : "Carica"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
